import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram, Play, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { AppleLogo } from "@/components/icons/AppleLogo";
import evgatewayLogo from "@/assets/evgateway-logo-transparent.png";
import fedrampLogo from "@/assets/certifications/fedramp-gray.png";
import soc2Logo from "@/assets/certifications/soc2-gray.png";
import iso27001Logo from "@/assets/certifications/iso27001-gray.png";
import ocaLogo from "@/assets/certifications/oca-gray.png";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const certifications = [
  { 
    name: "FedRAMP", 
    logo: fedrampLogo, 
    description: "Federal Risk and Authorization Management Program certified" 
  },
  { 
    name: "SOC 2 Type 2", 
    logo: soc2Logo, 
    description: "Service Organization Control 2 Type 2 compliant" 
  },
  { 
    name: "ISO 27001", 
    logo: iso27001Logo, 
    description: "Information Security Management System certified" 
  },
  { 
    name: "OCA", 
    logo: ocaLogo, 
    description: "Open Charge Alliance member" 
  },
];

const footerLinks = {
  solutions: [
    { name: "All Solutions", href: "/solutions" },
    { name: "Public Charging", href: "/solutions#public" },
    { name: "Fleet Charging", href: "/solutions#fleet" },
    { name: "V2G Technology", href: "/solutions#v2g" },
    { name: "Smart Charging", href: "/solutions#grid" },
  ],
  products: [
    { name: "SaaS Platform", href: "/products" },
    { name: "Mobile Apps", href: "/products#mobile" },
    { name: "Web Portal", href: "/products#portal" },
    { name: "API & Integrations", href: "/products#features" },
  ],
  company: [
    { name: "About Us", href: "/about" },
    { name: "CPO Partners", href: "/cpo" },
    { name: "For Drivers", href: "/drivers" },
    { name: "News", href: "/news" },
    { name: "Contact", href: "/contact" },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Terms of Use", href: "/terms-of-use" },
    { name: "Cookie Settings", href: "#", isCookieSettings: true },
  ],
};

export function Footer() {
  return (
    <footer className="relative bg-background/80 backdrop-blur-sm border-t border-border/30 pt-16 pb-8 overflow-hidden">
      {/* Ambient background */}
      <div 
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none opacity-30"
        style={{ 
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.1) 0%, transparent 70%)',
          filter: 'blur(80px)'
        }}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={evgatewayLogo} 
                alt="EVGateway"
                width={206}
                height={48} 
                className="h-12 w-auto logo-glow-pulse"
              />
            </Link>
            <p className="text-muted-foreground text-sm mb-4 max-w-xs leading-relaxed">
              The most secure and customizable EV charging management network. 
              Powering the future of electric mobility.
            </p>
            
            {/* Status Badge */}
            <a 
              href="https://evg-status.evgateway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-background/60 backdrop-blur-sm border border-border/30 px-3 py-1.5 rounded-full mb-6 hover:border-green-500/50 transition-colors"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-40 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-xs font-medium text-foreground">All Systems Operational</span>
            </a>
            
            <div className="space-y-2 text-sm text-muted-foreground mb-6">
              <a href="mailto:sales@evgateway.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Mail className="h-4 w-4" />
                sales@evgateway.com
              </a>
              <a href="tel:+1-949-945-6300" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Phone className="h-4 w-4" />
                949-945-6300
              </a>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>19681 Da Vinci, Foothill Ranch, CA 92610</span>
              </div>
            </div>
            
            {/* App Download Buttons */}
            <div className="flex items-center gap-3">
              <a 
                href="https://apps.apple.com/us/app/evgateway/id1202982043" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-foreground text-background rounded-xl px-3 py-2 hover:bg-foreground/90 hover:scale-105 transition-all duration-200"
              >
                <AppleLogo className="h-5 w-5" />
                <div className="text-left">
                  <div className="text-[8px] leading-tight opacity-80">Download on the</div>
                  <div className="text-xs font-semibold leading-tight">App Store</div>
                </div>
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.EVGATEWAY&hl=en_US" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-foreground text-background rounded-xl px-3 py-2 hover:bg-foreground/90 hover:scale-105 transition-all duration-200"
              >
                <Play className="h-5 w-5 fill-current" />
                <div className="text-left">
                  <div className="text-[8px] leading-tight opacity-80">Get it on</div>
                  <div className="text-xs font-semibold leading-tight">Google Play</div>
                </div>
              </a>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="font-semibold mb-4 text-foreground">Solutions</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-muted-foreground hover:text-primary transition-colors block py-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-semibold mb-4 text-foreground">Products</h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-muted-foreground hover:text-primary transition-colors block py-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold mb-4 text-foreground">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-muted-foreground hover:text-primary transition-colors block py-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal + Certifications */}
          <div>
            <h4 className="font-semibold mb-4 text-foreground">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  {link.isCookieSettings ? (
                    <button
                      onClick={() => {
                        if (typeof (window as any).showCookiePreferences === 'function') {
                          (window as any).showCookiePreferences();
                        }
                      }}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors block py-1 text-left"
                    >
                      {link.name}
                    </button>
                  ) : (
                    <Link 
                      to={link.href} 
                      className="text-sm text-muted-foreground hover:text-primary transition-colors block py-1"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            
            {/* Certification Badges */}
            <div className="mt-6">
              <h4 className="font-semibold mb-3 text-foreground text-sm">Certifications</h4>
              <div className="grid grid-cols-2 gap-3">
                {certifications.map((cert) => (
                  <Tooltip key={cert.name}>
                    <TooltipTrigger asChild>
                      <div className="cursor-pointer hover:scale-105 transition-transform" role="img" aria-label={cert.description}>
                        <img 
                          src={cert.logo} 
                          alt={`${cert.name} certification badge - ${cert.description}`}
                          className="w-16 h-16 object-contain opacity-80 hover:opacity-100 transition-opacity"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px]">
                      <p className="font-medium">{cert.name}</p>
                      <p className="text-xs text-muted-foreground">{cert.description}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border/30 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-4">
              <a href="https://linkedin.com/company/evgateway/mycompany/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded-sm" aria-label="Follow EVGateway on LinkedIn">
                <Linkedin className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href="https://x.com/EvGateway_EVG" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded-sm" aria-label="Follow EVGateway on X (Twitter)">
                <Twitter className="h-5 w-5" aria-hidden="true" />
              </a>
              <a href="https://www.instagram.com/evgateway/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded-sm" aria-label="Follow EVGateway on Instagram">
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
              <span className="text-border/50">|</span>
              <a 
                href="https://evg-status.evgateway.com/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors group" 
                aria-label="Service Status"
              >
                <Activity className="h-4 w-4 group-hover:text-green-500 transition-colors" />
                <span className="text-sm">Status</span>
              </a>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 EvGateway. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
